import { createApi } from '@reduxjs/toolkit/query/react';
import { ApiResponse } from '@/types';

import { customBaseQuery } from '@/lib/baseQuery';
import {
  IncomeDetailPayload,
  PersonalDetailPayload,
  PropertyDetailPayload,
  PurchaseRequestPayload,
  PurchaseSteps,
  StepFormResponse,
  YourPurchaseRequestPayload,
} from '../types/Purchase';
import {
  mapObjectKeysToCamelCase,
  mapObjectKeysToSnakeCase,
} from '@/utils/mapper';
import { PurchaseResponse } from '../routes/Purchase/types';

export const purchaseAPI = createApi({
  reducerPath: 'purchaseAPI',
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    postPurchaseRequest: builder.mutation<
      ApiResponse<any>,
      | PurchaseRequestPayload
      | PersonalDetailPayload
      | PropertyDetailPayload
      | IncomeDetailPayload
      | YourPurchaseRequestPayload
    >({
      query: ({ application_id, data, step, draft, broker_id }) => {
        const transformedData = mapObjectKeysToSnakeCase(data);
        return {
          url: 'api/purchase/v1/step/',
          method: 'POST',
          body: {
            application_id,
            data: transformedData,
            step,
            draft,
            broker_id,
          },
        };
      },
    }),
    getPurchaseStepDetail: builder.query<
      ApiResponse<StepFormResponse>,
      { application_id: string; step: PurchaseSteps }
    >({
      query: ({ application_id, step }) =>
        `api/purchase/v1/step/${application_id}/${step}`,
      transformResponse: (response: ApiResponse<any>) => {
        response.data.data = mapObjectKeysToCamelCase(response.data.data);
        return response;
      },
    }),
    getPurchaseRequestList: builder.query<
      ApiResponse<PurchaseResponse[]>,
      void
    >({
      query: () => `api/purchase/v1/assessment/list`,
    }),
    getPurchaseAssessmentDetail: builder.query<
      ApiResponse<any>,
      { application_id: string }
    >({
      query: ({ application_id }) =>
        `api/purchase/v1/assessment/${application_id}`,
    }),
    getPurchaseApplicationSummary: builder.query<
      ApiResponse<any>,
      { application_id: string }
    >({
      query: ({ application_id }) =>
        `api/purchase/v1/snapshot/${application_id}`,
    }),
  }),
});

export const {
  usePostPurchaseRequestMutation,
  useGetPurchaseStepDetailQuery,
  useGetPurchaseRequestListQuery,
  useGetPurchaseAssessmentDetailQuery,
  useGetPurchaseApplicationSummaryQuery,
} = purchaseAPI;
